import { alpha, Theme } from '@mui/material';
import { CSSObject } from '@mui/material/styles';

/**
 * Applied to the licence card an organisation has nominated for its constituents.
 * The highlight itself lives in the theme (see `recommendedLicenseCardStyles`) so both
 * palettes stay in step and the component only decides *whether* a card is recommended,
 * not what that looks like.
 */
export const RECOMMENDED_LICENSE_CARD_CLASS = 'license-card--recommended';

/**
 * Registers the angle the sweeping glow animates through.
 *
 * CSS cannot interpolate an unregistered custom property, so without this declaration
 * the conic gradient would jump in discrete steps rather than sweep. It is a global
 * at-rule rather than a component style, so it is mounted once at the theme root — that
 * also means the class above works anywhere it is applied, not just inside LicenseCard.
 * Where `@property` is unsupported the fallback in `var()` holds the angle at 0deg and
 * the rim renders as a static highlight: degraded, not broken.
 */
export const licenseCardGlobalStyles = {
	'@property --license-glow-angle': {
		syntax: "'<angle>'",
		inherits: 'false',
		initialValue: '0deg'
	}
} as Record<string, unknown>;

/**
 * Highlight for a nominated licence: a static accent ring, an ambient halo, and a bright
 * comet travelling around the rim.
 *
 * Shared by both palettes because it is derived entirely from `palette.primary`.
 *
 * Implementation notes, all of which are load-bearing:
 * - The ring is an `outline`, not a `border`. A border would widen the card by 2px and
 *   nudge every sibling in the grid; an outline consumes no layout space.
 * - It is not an inset `box-shadow` either. The card's first child is a full-bleed header
 *   image, and inset shadows paint below child content, so the rim would be clipped
 *   across the top of the card. Outlines paint above content.
 * - The comet is a full-cover conic gradient masked down to a 2px rim: paint the box
 *   twice and subtract the content box from the border box. The card clips its children,
 *   so the effect has to live inside the card rather than around it.
 * - `zIndex` lifts the card because siblings are opaque and painted later in DOM order,
 *   which would otherwise clip the halo bleeding rightward.
 */
export const recommendedLicenseCardStyles = (theme: Theme): CSSObject => {
	const accent = theme.palette.primary.main;

	return {
		[`&.${RECOMMENDED_LICENSE_CARD_CLASS}`]: {
			position: 'relative',
			zIndex: 1,
			outline: `2px solid ${alpha(accent, 0.55)}`,
			outlineOffset: '-2px',
			boxShadow: `0 0 0 5px ${alpha(accent, 0.18)}, 0 0 22px ${alpha(accent, 0.45)}, 0 10px 30px ${alpha(
				accent,
				0.3
			)}`,
			transition: 'transform 0.2s ease, box-shadow 0.2s ease',
			'&:hover': {
				// Restated so hovering intensifies the glow rather than replacing it with
				// the neutral card shadow.
				boxShadow: `0 0 0 6px ${alpha(accent, 0.24)}, 0 0 30px ${alpha(accent, 0.6)}, 0 14px 38px ${alpha(
					accent,
					0.4
				)}`
			},
			'@keyframes licenseCardGlowSweep': {
				to: { '--license-glow-angle': '360deg' }
			},
			'&::after': {
				content: '""',
				position: 'absolute',
				inset: 0,
				borderRadius: 'inherit',
				padding: '2px',
				background: `conic-gradient(from var(--license-glow-angle, 0deg), transparent 0deg, transparent 200deg, ${alpha(
					accent,
					0.5
				)} 290deg, ${theme.palette.common.white} 330deg, ${alpha(accent, 0.5)} 350deg, transparent 360deg)`,
				WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
				WebkitMaskComposite: 'xor',
				mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
				maskComposite: 'exclude',
				pointerEvents: 'none',
				zIndex: 2,
				animation: 'licenseCardGlowSweep 4s linear infinite',
				// Continuous motion is a vestibular trigger; honour the system setting.
				'@media (prefers-reduced-motion: reduce)': {
					animation: 'none'
				}
			}
		}
	};
};
